// modules/clientes/components/detalle/ClienteDatosTab.jsx
import React, { useState } from "react";
import {
  Box, Grid, Typography, TextField, MenuItem,
  Button, Alert, CircularProgress, Divider, Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import * as clienteService from "../../services/clienteService";

const DEPARTAMENTOS = [
  "La Paz", "Cochabamba", "Santa Cruz", "Oruro",
  "Potosí", "Chuquisaca", "Tarija", "Beni", "Pando",
];

const ESTADOS = ["Activo", "Inactivo", "Potencial", "PreVenta"];

const RUBROS = [
  "Entidad Bancaria", "Financiera", "Transporte Valores",
  "Transporte Internacional-Nacional", "Flotas y Buses",
  "Seguridad", "Distribuidora", "Eventos y Publicidad",
  "Institucion Gubernamental", "Constructora",
  "Consulado o Embajada", "Doctores Hospital",
  "Import/Export", "Servicios en General",
];

const compactTextFieldSx = {
  "& .MuiInputBase-input": {
    py: 0.75,
    fontSize: 13,
  },
  "& .MuiInputLabel-root": {
    fontSize: 13,
  },
  "& .MuiSelect-select": {
    py: 0.75,
  },
};

const Campo = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.35, wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const ClienteDatosTab = ({ cliente, onUpdate }) => {
  const [editando, setEditando] = useState(false);
  const [form,     setForm]     = useState({ ...cliente });
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleGuardar = async () => {
    setLoading(true);
    setError(null);
    try {
      await clienteService.update(cliente.cod_cliente, form);
      onUpdate();
      setEditando(false);
    } catch (err) {
      setError(err?.response?.data?.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = () => {
    setForm({ ...cliente });
    setError(null);
    setEditando(false);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>Información general</Typography>
        {!editando ? (
          <Button startIcon={<EditIcon />} size="small" onClick={() => setEditando(true)}>
            Editar
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" startIcon={<CancelIcon />} onClick={handleCancelar} disabled={loading}>
              Cancelar
            </Button>
            <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleGuardar} disabled={loading}>
              {loading ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Vista de solo lectura */}
      {!editando && (
        <Grid container spacing={1.25}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Campo label="Razón Social" value={cliente.razon_social} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Campo label="Código" value={cliente.cod_cliente} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Campo label="Estado" value={
              <Chip label={cliente.estado} size="small"
                color={cliente.estado === "Activo" ? "success" : "default"} />
            } />
          </Grid>

          <Grid size={{ xs: 12 }}><Divider sx={{ my: 0.25 }} /></Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Tipo persona" value={cliente.tipo_persona} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="C.I." value={cliente.ci} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Primer nombre" value={cliente.primer_nombre} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Segundo nombre" value={cliente.segundo_nombre} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Apellido paterno" value={cliente.apellido_paterno} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Apellido materno" value={cliente.apellido_materno} />
          </Grid>

          <Grid size={{ xs: 12 }}><Divider sx={{ my: 0.25 }} /></Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Campo label="Rubro" value={cliente.rubro} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Campo label="Departamento" value={cliente.departamento} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Campo label="Tipo de Cliente" value={cliente.tipo_cliente} />
          </Grid>

          <Grid size={{ xs: 12 }}><Divider sx={{ my: 0.25 }} /></Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Campo label="Dirección" value={cliente.direccion} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Campo label="Teléfono" value={cliente.telefono} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Campo label="Celular" value={cliente.celular} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Fax" value={cliente.fax} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Campo label="Correo Oficial" value={cliente.correo_ofi} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Campo label="Página Web" value={cliente.pagina_web} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Campo label="Observaciones" value={cliente.observaciones} />
          </Grid>
        </Grid>
      )}

      {/* Formulario de edición */}
      {editando && (
        <Grid container spacing={1.25}>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField select fullWidth size="small" label="Rubro o Actividad"
              name="rubro" value={form.rubro || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx}>
              <MenuItem value="">Sin rubro</MenuItem>
              {RUBROS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField select fullWidth size="small" label="Departamento"
              name="departamento" value={form.departamento || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx}>
              {DEPARTAMENTOS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField select fullWidth size="small" label="Estado"
              name="estado" value={form.estado || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx}>
              {ESTADOS.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField fullWidth size="small" label="Código"
              value={cliente.cod_cliente || ""} disabled sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField select fullWidth size="small" label="Tipo persona"
              name="tipo_persona" value={form.tipo_persona || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx}>
              <MenuItem value="">Sin tipo</MenuItem>
              {["Señor", "Señora", "Licenciado", "Licenciada", "Ingeniero", "Ingeniera", "Doctor", "Doctora"].map((tipo) => (
                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth size="small" label="C.I."
              name="ci" value={form.ci || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth size="small" label="Primer nombre"
              name="primer_nombre" value={form.primer_nombre || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth size="small" label="Segundo nombre"
              name="segundo_nombre" value={form.segundo_nombre || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth size="small" label="Apellido paterno"
              name="apellido_paterno" value={form.apellido_paterno || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField fullWidth size="small" label="Apellido materno"
              name="apellido_materno" value={form.apellido_materno || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth size="small" label="Dirección"
              name="direccion" value={form.direccion || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField fullWidth size="small" label="Teléfono"
              name="telefono" value={form.telefono || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField fullWidth size="small" label="Celular"
              name="celular" value={form.celular || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField fullWidth size="small" label="Fax"
              name="fax" value={form.fax || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth size="small" label="Correo Oficial" type="email"
              name="correo_ofi" value={form.correo_ofi || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth size="small" label="Página Web"
              name="pagina_web" value={form.pagina_web || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth size="small" multiline minRows={1} maxRows={3} label="Observaciones"
              name="observaciones" value={form.observaciones || ""} onChange={handleChange} disabled={loading} sx={compactTextFieldSx} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ClienteDatosTab;
